import React, { useMemo } from "react";
import {
  AlertTriangle,
  ArrowRight,
  Clock,
  Database,
  Loader2,
  RefreshCw,
  Shield,
  Swords,
  Users,
  Wallet,
  Zap,
} from "lucide-react";
import { getAllNavItems } from "../navConfig";
import NavIcon from "../components/NavIcon";

function formatNum(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function relativeTime(ts) {
  if (!ts) return "—";
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
}

function AlertRow({ alert }) {
  const icons = {
    critical: AlertTriangle,
    warning: AlertTriangle,
    info: Zap,
  };
  const Icon = icons[alert.level] || Zap;

  return (
    <div className={`admin-alert-card level-${alert.level}`}>
      <span className={`admin-alert-icon level-${alert.level}`}>
        <Icon size={16} strokeWidth={2.2} />
      </span>
      <div className="admin-alert-body">
        <p className="admin-alert-title">{alert.title}</p>
        <p className="admin-alert-msg">{alert.message}</p>
      </div>
      {alert.count != null ? (
        <span className="admin-alert-count">{alert.count}</span>
      ) : null}
    </div>
  );
}

function MetricCard({ label, value, sub, accent, icon: Icon }) {
  return (
    <article className="admin-metric-card" style={{ "--metric-accent": accent }}>
      <div className="admin-metric-head">
        {Icon ? (
          <span className="admin-metric-icon">
            <Icon size={15} strokeWidth={2.2} />
          </span>
        ) : null}
        <p className="admin-metric-label">{label}</p>
      </div>
      <p className="admin-metric-value">{value}</p>
      {sub ? <p className="admin-metric-sub">{sub}</p> : null}
    </article>
  );
}

function QuickDeskCard({ item, onNavigate }) {
  return (
    <button type="button" className="admin-desk-card" onClick={() => onNavigate(item.id)}>
      <span className="admin-desk-card-icon">
        <NavIcon id={item.id} size={18} />
      </span>
      <span className="admin-desk-card-copy">
        <span className="admin-desk-card-label">{item.label}</span>
        {item.hint ? <span className="admin-desk-card-hint">{item.hint}</span> : null}
      </span>
      <ArrowRight size={14} className="admin-desk-card-arrow" aria-hidden />
    </button>
  );
}

export default function OverviewPage({
  overview,
  loading,
  lastFetchedAt,
  onRefresh,
  onNavigate,
  userRole,
}) {
  const navItems = useMemo(() => getAllNavItems(userRole).filter((i) => i.id !== "overview"), [userRole]);

  const users = overview?.users || {};
  const p2p = overview?.p2p || {};
  const payments = overview?.payments || {};
  const system = overview?.system || {};
  const alerts = overview?.alerts || [];
  const systemOk = Boolean(system.redis);

  return (
    <div className="admin-overview">
      <div className="admin-overview-aurora" aria-hidden>
        <div className="admin-overview-aurora-a" />
        <div className="admin-overview-aurora-b" />
      </div>

      <div className="admin-overview-inner">
        <header className="admin-overview-hero">
          <div className="admin-overview-hero-copy">
            <p className="admin-overview-eyebrow">Mission control</p>
            <h1 className="admin-overview-title">Platform overview</h1>
            <p className="admin-overview-sub">
              Live snapshot of users, bets, payments, and system health.
            </p>
          </div>
          <div className="admin-overview-hero-actions">
            <div className={`admin-status-pill ${systemOk ? "is-live" : "is-down"}`}>
              <span className="admin-pulse-dot is-live" aria-hidden />
              {systemOk ? "Systems online" : "Degraded"}
            </div>
            <button
              type="button"
              className="admin-overview-refresh"
              onClick={onRefresh}
              disabled={loading}
            >
              {loading ? (
                <Loader2 size={15} className="admin-sync-spin" />
              ) : (
                <RefreshCw size={15} strokeWidth={2.2} />
              )}
              Sync
              <span className="admin-overview-refresh-time">{relativeTime(lastFetchedAt)}</span>
            </button>
          </div>
        </header>

        {loading && !overview ? (
          <div className="admin-overview-loading">
            <Loader2 size={28} className="admin-sync-spin" />
            <p>Loading platform data…</p>
          </div>
        ) : (
          <>
            {alerts.length > 0 ? (
              <section className="admin-overview-section" aria-label="Alerts">
                <h2 className="admin-section-title">Requires attention</h2>
                <div className="admin-alert-stack">
                  {alerts.map((alert) => (
                    <AlertRow key={alert.code} alert={alert} />
                  ))}
                </div>
              </section>
            ) : null}

            <section className="admin-overview-section" aria-label="Key metrics">
              <h2 className="admin-section-title">Key metrics</h2>
              <div className="admin-metric-grid">
                <MetricCard
                  label="Total users"
                  value={formatNum(users.total)}
                  sub={`+${formatNum(users.new24h)} today · +${formatNum(users.new7d)} this week`}
                  accent="#1E88E5"
                  icon={Users}
                />
                <MetricCard
                  label="Active P2P duels"
                  value={formatNum(p2p.active)}
                  sub={`${formatNum(p2p.pendingOutcome)} awaiting outcome`}
                  accent="#64B5F6"
                  icon={Swords}
                />
                <MetricCard
                  label="Transactions (24h)"
                  value={formatNum(payments.transactions24h)}
                  sub={`${formatNum(payments.pendingWithdrawals)} withdrawals · ${formatNum(payments.pendingDeposits)} deposits pending`}
                  accent="#2979FF"
                  icon={Wallet}
                />
                <MetricCard
                  label="System jobs"
                  value={formatNum(system.jobCount)}
                  sub={
                    system.nextJob
                      ? `Next: ${system.nextJob.name} in ${Math.max(0, Math.round((system.nextJob.msUntilNext || 0) / 60000))}m`
                      : "No scheduled jobs"
                  }
                  accent={systemOk ? "#2ECC71" : "#EF5350"}
                  icon={Database}
                />
              </div>
            </section>

            <section className="admin-overview-section" aria-label="Platform health">
              <h2 className="admin-section-title">Platform health</h2>
              <div className="admin-health-grid">
                <div className="admin-health-card">
                  <span className="admin-health-icon tone-blue">
                    <Users size={16} />
                  </span>
                  <div>
                    <p className="admin-health-label">User base</p>
                    <p className="admin-health-value">
                      {formatNum(users.active)} active
                      <span className="admin-health-sep">·</span>
                      {formatNum(users.banned)} banned
                    </p>
                  </div>
                </div>
                <div className="admin-health-card">
                  <span className="admin-health-icon tone-green">
                    <Swords size={16} />
                  </span>
                  <div>
                    <p className="admin-health-label">P2P (24h)</p>
                    <p className="admin-health-value">
                      {formatNum(p2p.settled24h)} settled
                      <span className="admin-health-sep">·</span>
                      {formatNum(p2p.voided24h)} voided
                    </p>
                  </div>
                </div>
                <div className="admin-health-card">
                  <span className="admin-health-icon tone-amber">
                    <Shield size={16} />
                  </span>
                  <div>
                    <p className="admin-health-label">Compliance</p>
                    <p className="admin-health-value">
                      {formatNum(payments.onHold)} on hold
                      <span className="admin-health-sep">·</span>
                      {formatNum(users.unverified)} unverified
                    </p>
                  </div>
                </div>
                <div className="admin-health-card">
                  <span className="admin-health-icon tone-purple">
                    <Clock size={16} />
                  </span>
                  <div>
                    <p className="admin-health-label">Staff & sync</p>
                    <p className="admin-health-value">
                      {formatNum(users.staff)} staff
                      <span className="admin-health-sep">·</span>
                      synced {relativeTime(lastFetchedAt)}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section className="admin-overview-section" aria-label="Quick access">
              <h2 className="admin-section-title">Jump to desk</h2>
              <div className="admin-desk-grid">
                {navItems.map((item) => (
                  <QuickDeskCard key={item.id} item={item} onNavigate={onNavigate} />
                ))}
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
